import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Divider,
  Grid,
  Button,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  SelectChangeEvent,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  DonutLarge as DonutLargeIcon,
} from "@mui/icons-material";
import { Doughnut, Bar, Radar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
  RadialLinearScale,
} from "chart.js";
import { Evaluation } from "../../types";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  RadialLinearScale,
);

interface EvaluationResultsProps {
  evaluation: Evaluation;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`evaluation-results-tabpanel-${index}`}
      aria-labelledby={`evaluation-results-tab-${index}`}
      {...other}
      style={{ padding: "16px 0" }}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const EvaluationResults: React.FC<EvaluationResultsProps> = ({
  evaluation,
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [comparisonView, setComparisonView] = useState<string>("providers");
  const [chartType, setChartType] = useState<string>("bar");
  const [selectedMetric, setSelectedMetric] = useState<string>("success");

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowId: string) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  // Handle comparison view change
  const handleComparisonViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: string | null,
  ) => {
    if (newView !== null) {
      setComparisonView(newView);
    }
  };

  // Handle chart type change
  const handleChartTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newType: string | null,
  ) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  // Handle metric change
  const handleMetricChange = (event: SelectChangeEvent<string>) => {
    setSelectedMetric(event.target.value);
  };

  // Extract results data for visualization
  const extractResultsData = () => {
    if (!evaluation.results || !evaluation.results.results) {
      return { successCount: 0, failureCount: 0, results: [] };
    }

    const results = evaluation.results.results as any[];
    const successCount = results.filter((r) => r.success).length;
    const failureCount = results.length - successCount;

    return { successCount, failureCount, results };
  };

  const { successCount, failureCount, results } = extractResultsData();

  // Process data for provider comparisons
  const providerComparisonData = useMemo(() => {
    if (!results.length) return null;

    // Get unique providers
    const providers = Array.from(
      new Set(
        results.map((r) =>
          typeof r.provider === "object"
            ? r.provider.label || r.provider.id
            : r.provider,
        ),
      ),
    );

    // Count successes for each provider
    const providerStats = providers.map((provider) => {
      const providerResults = results.filter((r) => {
        const resultProvider =
          typeof r.provider === "object"
            ? r.provider.label || r.provider.id
            : r.provider;
        return resultProvider === provider;
      });

      const totalTests = providerResults.length;
      const successfulTests = providerResults.filter((r) => r.success).length;
      const successRate = totalTests
        ? Math.round((successfulTests / totalTests) * 100)
        : 0;

      // Calculate average response time (if available)
      let avgResponseTime = 0;
      const responseTimes = providerResults
        .map((r) => r.responseTime || r.latency)
        .filter(Boolean);

      if (responseTimes.length) {
        avgResponseTime = Math.round(
          responseTimes.reduce((sum, time) => sum + time, 0) /
            responseTimes.length,
        );
      }

      return {
        provider,
        totalTests,
        successfulTests,
        failedTests: totalTests - successfulTests,
        successRate,
        avgResponseTime,
      };
    });

    return {
      providers,
      stats: providerStats,
    };
  }, [results]);

  // Process data for test case comparisons
  const testCaseComparisonData = useMemo(() => {
    if (!results.length) return null;

    // Get unique test cases
    const testCases = Array.from(
      new Set(
        results.map((r, i) =>
          typeof r.testCase === "object"
            ? r.testCase.label || `Test ${i + 1}`
            : r.testCase || `Test ${i + 1}`,
        ),
      ),
    );

    // Count successes for each test case across providers
    const testCaseStats = testCases.map((testCase) => {
      const testCaseResults = results.filter((r, i) => {
        const resultTestCase =
          typeof r.testCase === "object"
            ? r.testCase.label || `Test ${i + 1}`
            : r.testCase || `Test ${i + 1}`;
        return resultTestCase === testCase;
      });

      const totalTests = testCaseResults.length;
      const successfulTests = testCaseResults.filter((r) => r.success).length;
      const successRate = totalTests
        ? Math.round((successfulTests / totalTests) * 100)
        : 0;

      // Get provider-specific results for this test case
      const providers = Array.from(
        new Set(
          testCaseResults.map((r) =>
            typeof r.provider === "object"
              ? r.provider.label || r.provider.id
              : r.provider,
          ),
        ),
      );

      const providerResults = providers.map((provider) => {
        const providerTestResults = testCaseResults.filter((r) => {
          const resultProvider =
            typeof r.provider === "object"
              ? r.provider.label || r.provider.id
              : r.provider;
          return resultProvider === provider;
        });

        return {
          provider,
          success: providerTestResults.some((r) => r.success),
        };
      });

      return {
        testCase,
        totalTests,
        successfulTests,
        failedTests: totalTests - successfulTests,
        successRate,
        providerResults,
      };
    });

    return {
      testCases,
      stats: testCaseStats,
    };
  }, [results]);

  // Generate chart data based on selected view and metric
  const generateChartData = useMemo(() => {
    if (comparisonView === "providers" && providerComparisonData) {
      // Provider comparison chart data
      const labels = providerComparisonData.providers;
      let data;
      let label;

      switch (selectedMetric) {
        case "success":
          data = providerComparisonData.stats.map((s) => s.successRate);
          label = "Success Rate (%)";
          break;
        case "total":
          data = providerComparisonData.stats.map((s) => s.totalTests);
          label = "Total Tests";
          break;
        case "response_time":
          data = providerComparisonData.stats.map((s) => s.avgResponseTime);
          label = "Avg Response Time (ms)";
          break;
        default:
          data = providerComparisonData.stats.map((s) => s.successRate);
          label = "Success Rate (%)";
      }

      return {
        labels,
        datasets: [
          {
            label,
            data,
            backgroundColor:
              selectedMetric === "success"
                ? providerComparisonData.stats.map((s) => {
                    const percentage = s.successRate / 100;
                    return `rgba(${Math.round(255 * (1 - percentage))}, ${Math.round(255 * percentage)}, 0, 0.7)`;
                  })
                : theme.palette.primary.main,
            borderWidth: 1,
            borderColor: theme.palette.divider,
          },
        ],
      };
    } else if (comparisonView === "tests" && testCaseComparisonData) {
      // Test case comparison chart data
      const labels = testCaseComparisonData.testCases;
      let data;
      let label;

      switch (selectedMetric) {
        case "success":
          data = testCaseComparisonData.stats.map((s) => s.successRate);
          label = "Success Rate (%)";
          break;
        case "total":
          data = testCaseComparisonData.stats.map((s) => s.totalTests);
          label = "Number of Providers";
          break;
        default:
          data = testCaseComparisonData.stats.map((s) => s.successRate);
          label = "Success Rate (%)";
      }

      return {
        labels,
        datasets: [
          {
            label,
            data,
            backgroundColor:
              selectedMetric === "success"
                ? testCaseComparisonData.stats.map((s) => {
                    const percentage = s.successRate / 100;
                    return `rgba(${Math.round(255 * (1 - percentage))}, ${Math.round(255 * percentage)}, 0, 0.7)`;
                  })
                : theme.palette.primary.main,
            borderWidth: 1,
            borderColor: theme.palette.divider,
          },
        ],
      };
    }

    // Heat map data for test cases vs providers
    if (
      comparisonView === "matrix" &&
      testCaseComparisonData &&
      providerComparisonData
    ) {
      const providers = providerComparisonData.providers;
      const testCases = testCaseComparisonData.testCases;

      // Create datasets for each provider
      const datasets = providers.map((provider, index) => {
        // For each provider, get success rate for each test case
        const data = testCaseComparisonData.stats.map((testStat) => {
          const providerResult = testStat.providerResults.find(
            (pr) => pr.provider === provider,
          );
          return providerResult && providerResult.success ? 100 : 0;
        });

        return {
          label: provider,
          data,
          backgroundColor: `hsl(${(index * 360) / providers.length}, 70%, 60%)`,
          borderColor: `hsl(${(index * 360) / providers.length}, 70%, 50%)`,
          borderWidth: 1,
          fill: chartType === "radar",
          pointBackgroundColor: `hsl(${(index * 360) / providers.length}, 70%, 50%)`,
        };
      });

      return {
        labels: testCases,
        datasets,
      };
    }

    return null;
  }, [
    comparisonView,
    selectedMetric,
    providerComparisonData,
    testCaseComparisonData,
    chartType,
    theme.palette,
  ]);

  // Chart options
  const chartOptions = useMemo(() => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            usePointStyle: true,
            padding: 20,
          },
        },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              const label = context.dataset.label || "";
              const value = context.raw || 0;

              if (selectedMetric === "success") {
                return `${label}: ${value}%`;
              }
              if (selectedMetric === "response_time") {
                return `${label}: ${value}ms`;
              }
              return `${label}: ${value}`;
            },
          },
        },
      },
    };

    // Additional options for bar charts
    if (chartType === "bar") {
      return {
        ...baseOptions,
        scales: {
          y: {
            beginAtZero: true,
            ...(selectedMetric === "success" && { max: 100 }),
            title: {
              display: true,
              text:
                selectedMetric === "success"
                  ? "Success Rate (%)"
                  : selectedMetric === "response_time"
                    ? "Response Time (ms)"
                    : "Count",
            },
          },
        },
        indexAxis:
          comparisonView === "tests" &&
          testCaseComparisonData &&
          testCaseComparisonData.testCases.length > 5
            ? ("y" as const)
            : ("x" as const),
      };
    }

    // Options for radar charts
    if (chartType === "radar") {
      return {
        ...baseOptions,
        scales: {
          r: {
            beginAtZero: true,
            max: 100,
            ticks: {
              stepSize: 20,
            },
          },
        },
      };
    }

    // Options for line charts
    if (chartType === "line") {
      return {
        ...baseOptions,
        scales: {
          y: {
            beginAtZero: true,
            ...(selectedMetric === "success" && { max: 100 }),
            title: {
              display: true,
              text:
                selectedMetric === "success"
                  ? "Success Rate (%)"
                  : selectedMetric === "response_time"
                    ? "Response Time (ms)"
                    : "Count",
            },
          },
        },
      };
    }

    // Options for pie/doughnut charts
    if (chartType === "pie") {
      return {
        ...baseOptions,
        cutout: "70%",
      };
    }

    return baseOptions;
  }, [chartType, selectedMetric, testCaseComparisonData, comparisonView]);

  // Render appropriate chart based on type
  const renderChart = () => {
    if (!generateChartData) {
      return (
        <Typography variant="body1" color="text.secondary">
          No data available for visualization
        </Typography>
      );
    }

    switch (chartType) {
      case "bar":
        return <Bar data={generateChartData} options={chartOptions as any} />;
      case "pie":
        return (
          <Doughnut data={generateChartData} options={chartOptions as any} />
        );
      case "radar":
        return <Radar data={generateChartData} options={chartOptions as any} />;
      case "line":
        return <Line data={generateChartData} options={chartOptions as any} />;
      default:
        return <Bar data={generateChartData} options={chartOptions as any} />;
    }
  };

  // Data for success rate chart
  const successRateData = {
    labels: ["Success", "Failure"],
    datasets: [
      {
        data: [successCount, failureCount],
        backgroundColor: [theme.palette.success.main, theme.palette.error.main],
        borderWidth: 0,
      },
    ],
  };

  // Options for success rate chart
  const successRateOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = successCount + failureCount;
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    cutout: "70%",
  };

  // Mock data for provider comparison chart (in a real app, this would be generated from results)
  const providerData = {
    labels: ["OpenAI (GPT-4o)", "Anthropic (Claude 3)", "Google (Gemini)"],
    datasets: [
      {
        label: "Success Rate (%)",
        data: [92, 86, 78],
        backgroundColor: theme.palette.primary.main,
      },
    ],
  };

  // Options for provider comparison chart
  const providerOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Success Rate (%)",
        },
      },
    },
  };

  // Handle download results
  const handleDownloadResults = () => {
    if (!evaluation.results) return;

    const resultsJson = JSON.stringify(evaluation.results, null, 2);
    const blob = new Blob([resultsJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `evaluation-${evaluation._id}-results.json`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Evaluation Results</Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadResults}
          disabled={!evaluation.results}
        >
          Download Results
        </Button>
      </Box>

      <Divider />

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="evaluation results tabs"
          sx={{ px: 2 }}
        >
          <Tab label="Summary" />
          <Tab label="Details" />
          <Tab label="Comparisons" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                variant="outlined"
                sx={{ height: "100%", borderRadius: 2 }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Success Rate
                  </Typography>
                  <Box
                    sx={{
                      height: 300,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {results.length > 0 ? (
                      <Box
                        sx={{ width: "100%", height: "100%", maxWidth: 300 }}
                      >
                        <Doughnut
                          data={successRateData}
                          options={successRateOptions}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        No results available
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {results.length > 0
                        ? `${Math.round((successCount / results.length) * 100)}%`
                        : "0%"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Overall Success Rate
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                variant="outlined"
                sx={{ height: "100%", borderRadius: 2 }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Provider Comparison
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    {results.length > 0 ? (
                      <Bar data={providerData} options={providerOptions} />
                    ) : (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body1" color="text.secondary">
                          No comparison data available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Summary Statistics
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Total Tests
                      </Typography>
                      <Typography variant="h6">{results.length}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Success Count
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {successCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Failure Count
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {failureCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Providers
                      </Typography>
                      <Typography variant="h6">
                        {evaluation.providerIds?.length || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ p: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Test</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.length > 0 ? (
                  results.map((result, index) => (
                    <React.Fragment key={index}>
                      <TableRow hover>
                        <TableCell>
                          {typeof result.testCase === "object"
                            ? result.testCase?.label || `Test ${index + 1}`
                            : result.testCase || `Test ${index + 1}`}
                        </TableCell>
                        <TableCell>
                          {typeof result.provider === "object"
                            ? result.provider.label ||
                              result.provider.id ||
                              "Unknown"
                            : result.provider || "Unknown"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={
                              result.success ? <CheckIcon /> : <CloseIcon />
                            }
                            label={result.success ? "Success" : "Failure"}
                            color={result.success ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() =>
                              toggleRowExpansion(`result-${index}`)
                            }
                            aria-expanded={expandedRow === `result-${index}`}
                            aria-label="show details"
                          >
                            {expandedRow === `result-${index}` ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={4}
                        >
                          <Collapse
                            in={expandedRow === `result-${index}`}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ margin: 2 }}>
                              <Typography
                                variant="h6"
                                gutterBottom
                                component="div"
                              >
                                Details
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2">
                                    Prompt
                                  </Typography>
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 2,
                                      mb: 2,
                                      bgcolor: "background.paper",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      component="pre"
                                      sx={{ whiteSpace: "pre-wrap" }}
                                    >
                                      {typeof result.prompt === "object"
                                        ? JSON.stringify(result.prompt, null, 2)
                                        : result.prompt ||
                                          "No prompt data available"}
                                    </Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2">
                                    Response
                                  </Typography>
                                  <Paper
                                    variant="outlined"
                                    sx={{ p: 2, bgcolor: "background.paper" }}
                                  >
                                    <Typography
                                      variant="body2"
                                      component="pre"
                                      sx={{ whiteSpace: "pre-wrap" }}
                                    >
                                      {typeof result.response === "object"
                                        ? JSON.stringify(
                                            result.response,
                                            null,
                                            2,
                                          )
                                        : result.response ||
                                          "No response data available"}
                                    </Typography>
                                  </Paper>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ py: 3 }}
                      >
                        No results available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ p: 2 }}>
          {results.length > 0 ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", md: "center" },
                  mb: 3,
                  gap: 2,
                }}
              >
                <ToggleButtonGroup
                  value={comparisonView}
                  exclusive
                  onChange={handleComparisonViewChange}
                  aria-label="comparison view"
                  size="small"
                >
                  <ToggleButton value="providers">By Provider</ToggleButton>
                  <ToggleButton value="tests">By Test Case</ToggleButton>
                  <ToggleButton value="matrix">Success Matrix</ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 150 }}
                    disabled={comparisonView === "matrix"}
                  >
                    <InputLabel id="metric-select-label">Metric</InputLabel>
                    <Select
                      labelId="metric-select-label"
                      value={selectedMetric}
                      onChange={handleMetricChange}
                      label="Metric"
                    >
                      <MenuItem value="success">Success Rate</MenuItem>
                      <MenuItem value="total">Total Tests</MenuItem>
                      {comparisonView === "providers" && (
                        <MenuItem value="response_time">Response Time</MenuItem>
                      )}
                    </Select>
                  </FormControl>

                  <ToggleButtonGroup
                    value={chartType}
                    exclusive
                    onChange={handleChartTypeChange}
                    aria-label="chart type"
                    size="small"
                  >
                    <ToggleButton value="bar">
                      <BarChartIcon />
                    </ToggleButton>
                    {comparisonView !== "matrix" && (
                      <ToggleButton value="pie">
                        <PieChartIcon />
                      </ToggleButton>
                    )}
                    {comparisonView === "matrix" && (
                      <ToggleButton value="radar">
                        <DonutLargeIcon />
                      </ToggleButton>
                    )}
                    <ToggleButton value="line">
                      <TimelineIcon />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>

              <Card
                elevation={0}
                variant="outlined"
                sx={{ borderRadius: 2, height: 400, p: 2 }}
              >
                <Box sx={{ height: "100%" }}>{renderChart()}</Box>
              </Card>

              <Grid container spacing={3} sx={{ mt: 2 }}>
                {comparisonView === "providers" && providerComparisonData && (
                  <Grid item xs={12}>
                    <Card
                      elevation={0}
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Provider Performance Breakdown
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Provider</TableCell>
                                <TableCell>Total Tests</TableCell>
                                <TableCell>Success Rate</TableCell>
                                <TableCell>Success/Failure</TableCell>
                                {providerComparisonData.stats.some(
                                  (s) => s.avgResponseTime > 0,
                                ) && <TableCell>Avg Response Time</TableCell>}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {providerComparisonData.stats.map(
                                (stat, index) => (
                                  <TableRow key={index} hover>
                                    <TableCell>
                                      <Typography fontWeight="medium">
                                        {stat.provider}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>{stat.totalTests}</TableCell>
                                    <TableCell>
                                      <Typography
                                        fontWeight="medium"
                                        color={
                                          stat.successRate > 70
                                            ? "success.main"
                                            : stat.successRate > 40
                                              ? "warning.main"
                                              : "error.main"
                                        }
                                      >
                                        {stat.successRate}%
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {stat.successfulTests}/{stat.failedTests}
                                    </TableCell>
                                    {providerComparisonData.stats.some(
                                      (s) => s.avgResponseTime > 0,
                                    ) && (
                                      <TableCell>
                                        {stat.avgResponseTime
                                          ? `${stat.avgResponseTime}ms`
                                          : "N/A"}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ),
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {comparisonView === "tests" && testCaseComparisonData && (
                  <Grid item xs={12}>
                    <Card
                      elevation={0}
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Test Case Performance Breakdown
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Test Case</TableCell>
                                <TableCell>Providers</TableCell>
                                <TableCell>Success Rate</TableCell>
                                <TableCell>Success/Failure</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {testCaseComparisonData.stats.map(
                                (stat, index) => (
                                  <TableRow key={index} hover>
                                    <TableCell>
                                      <Typography fontWeight="medium">
                                        {stat.testCase}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>{stat.totalTests}</TableCell>
                                    <TableCell>
                                      <Typography
                                        fontWeight="medium"
                                        color={
                                          stat.successRate > 70
                                            ? "success.main"
                                            : stat.successRate > 40
                                              ? "warning.main"
                                              : "error.main"
                                        }
                                      >
                                        {stat.successRate}%
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {stat.successfulTests}/{stat.failedTests}
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {comparisonView === "matrix" &&
                  testCaseComparisonData &&
                  providerComparisonData && (
                    <Grid item xs={12}>
                      <Card
                        elevation={0}
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                      >
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Provider vs Test Case Success Matrix
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Test Case</TableCell>
                                  {providerComparisonData.providers.map(
                                    (provider, index) => (
                                      <TableCell key={index}>
                                        {provider}
                                      </TableCell>
                                    ),
                                  )}
                                  <TableCell>Overall Success</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {testCaseComparisonData.stats.map(
                                  (stat, index) => (
                                    <TableRow key={index} hover>
                                      <TableCell>
                                        <Typography fontWeight="medium">
                                          {stat.testCase}
                                        </Typography>
                                      </TableCell>
                                      {providerComparisonData.providers.map(
                                        (provider, idx) => {
                                          const providerResult =
                                            stat.providerResults.find(
                                              (pr) => pr.provider === provider,
                                            );
                                          return (
                                            <TableCell key={idx}>
                                              {providerResult ? (
                                                <Chip
                                                  icon={
                                                    providerResult.success ? (
                                                      <CheckIcon />
                                                    ) : (
                                                      <CloseIcon />
                                                    )
                                                  }
                                                  label={
                                                    providerResult.success
                                                      ? "Pass"
                                                      : "Fail"
                                                  }
                                                  color={
                                                    providerResult.success
                                                      ? "success"
                                                      : "error"
                                                  }
                                                  size="small"
                                                />
                                              ) : (
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                >
                                                  N/A
                                                </Typography>
                                              )}
                                            </TableCell>
                                          );
                                        },
                                      )}
                                      <TableCell>
                                        <Typography
                                          fontWeight="medium"
                                          color={
                                            stat.successRate > 70
                                              ? "success.main"
                                              : stat.successRate > 40
                                                ? "warning.main"
                                                : "error.main"
                                          }
                                        >
                                          {stat.successRate}%
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ),
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
              </Grid>
            </>
          ) : (
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ py: 3 }}
            >
              No results available for comparison
            </Typography>
          )}
        </Box>
      </TabPanel>
    </Paper>
  );
};

export default EvaluationResults;
